import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { loadFontStylesheets } from '../utils/fonts';
import { InvitationPreview } from '../components/InvitationPreview';
import { PageLoader } from '../components/Spinner';
import { Button } from '../components/ui/button';
import { useDocumentTitle } from '../hooks/use-document-title';

/**
 * Shown when a share link can't be loaded. `networkError` distinguishes
 * "server unreachable" (offer a retry) from a genuinely missing invitation.
 */
export const InvitationNotFound = ({ networkError = false }) => (
  <main id="main-content" className="min-h-screen flex items-center justify-center bg-paper paper-texture px-4">
    <div className="text-center max-w-md">
      <h1 className="font-serif text-2xl sm:text-3xl font-bold text-ink mb-2">
        {networkError ? "We couldn't load this invitation" : 'Invitation not found'}
      </h1>
      <p className="text-ink/60 mb-6">
        {networkError
          ? 'Please check your connection and try again in a moment.'
          : 'This invitation link may have expired or been removed. Please check with the host.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {networkError && (
          <Button onClick={() => window.location.reload()} className="bg-ink text-paper hover:bg-ink/90">
            Try again
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/">Visit Azamein</Link>
        </Button>
      </div>
    </div>
  </main>
);

export default function SharePage() {
  const { shareLink } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);

  useDocumentTitle(data?.invitation?.name || "You're invited");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/share/${shareLink}`)
      .then((response) => {
        if (!cancelled) setData(response.data);
        loadFontStylesheets(response.data?.template?.fonts); // a partner template's own fonts
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setNetworkError(!err.response);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Invitation text uses the fonts chosen in the editor.
    axios
      .get(`${API_URL}/fonts`)
      .then((response) => loadFontStylesheets(response.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [shareLink]);

  if (loading) {
    return <PageLoader label="Opening invitation" />;
  }

  if (!data?.invitation) {
    return <InvitationNotFound networkError={networkError} />;
  }

  const { invitation, template, show_watermark } = data;

  return (
    <main id="main-content" className="min-h-screen bg-muted flex items-center justify-center px-4 py-8 sm:p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full"
      >
        <h1 className="sr-only">{invitation.name}</h1>
        {!show_watermark && invitation.server_image_url ? (
          // Paid: the finished picture rendered by the server (the clean artwork itself isn't public).
          <img
            src={invitation.server_image_url}
            alt={`${invitation.name} invitation`}
            className="w-full h-auto bg-white rounded-lg shadow-floating"
            data-testid="shared-invitation"
          />
        ) : (
          <InvitationPreview
            backgroundImage={template?.display_image || template?.background_image}
            fields={invitation.text_fields}
            elements={invitation.elements}
            background={invitation.background}
            perspective={template?.perspective}
            showWatermark={show_watermark}
            alt={`${invitation.name} invitation`}
            eager
            className="bg-white rounded-lg shadow-floating"
            data-testid="shared-invitation"
          />
        )}

        <div className="text-center mt-6">
          <p className="text-ink/60 text-sm">
            Created with{' '}
            <Link to="/" className="font-script text-gold text-lg hover:underline">
              Azamein
            </Link>
          </p>
        </div>
      </motion.div>
    </main>
  );
}
